import { type RefObject, useEffect } from 'react';

/**
 * Registry of elements contributing to the masthead height.
 * Each contributor stores its bottom position (px from viewport top).
 * The CSS variable `--bnef-masthead-size` on <body> is always set to the
 * largest bottom value, representing the lowest edge of all top-stuck elements.
 */
const contributors = new Map<string, number>();

const updateCssVariable = () => {
    const values = [...contributors.values()];
    const max = values.length > 0 ? Math.max(...values) : 0;
    document.body.style.setProperty('--bnef-masthead-size', `${max}px`);

    // Set a per-contributor CSS variable so other elements can reference
    // the bottom position of a specific masthead contributor.
    contributors.forEach((value, key) => {
        document.body.style.setProperty(`--bnef-masthead-size-${key}`, `${value}px`);
    });
};

/**
 * Registers an element as a masthead contributor. Measures the element's
 * bottom position and updates the global `--bnef-masthead-size` CSS variable
 * on `<body>` with the maximum bottom value across all contributors.
 *
 * - Only the largest value is written (handles race conditions between contributors)
 * - On window resize, this contributor re-measures itself (each contributor
 *   does the same independently)
 * - On unmount, the contributor is removed and the variable is recalculated
 *
 * Measurement is deferred to the next animation frame to ensure layout has
 * settled. This matters on mobile Safari / mobile Chrome where the `resize`
 * event fires mid-animation as the address bar collapses/expands; a
 * synchronous `getBoundingClientRect()` at that moment can read a stale or
 * zero `bottom` value.
 *
 * @param id A unique identifier for this contributor (e.g. 'navigation-header', 'clothesline')
 * @param ref A ref to the DOM element to measure
 */
export const useMastheadSize = (id: string, ref: RefObject<HTMLElement | null>) => {
    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        let rafId: number | null = null;

        const measure = () => {
            const rect = element.getBoundingClientRect();
            contributors.set(id, rect.bottom);
            updateCssVariable();
        };

        const scheduleMeasure = () => {
            if (rafId !== null) {
                return;
            }
            rafId = requestAnimationFrame(() => {
                rafId = null;
                measure();
            });
        };

        // Initial measurement is synchronous so the variable is set ASAP, but
        // we also schedule a deferred re-measure to catch late layout.
        measure();
        scheduleMeasure();

        const resizeObserver = new ResizeObserver(scheduleMeasure);
        resizeObserver.observe(element);

        // ResizeObserver only fires when the observed element's *size* changes.
        // A window resize (or mobile URL-bar collapse) can change the element's
        // position relative to the viewport without changing its size, so we
        // listen to resize too and re-measure ourselves (never touching other
        // contributors' values).
        window.addEventListener('resize', scheduleMeasure, { passive: true });

        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
            window.removeEventListener('resize', scheduleMeasure);
            contributors.delete(id);
            document.body.style.removeProperty(`--bnef-masthead-size-${id}`);
            updateCssVariable();
        };
    }, [id, ref]);
};
